import type { ComponentProps } from 'react';
import React from 'react';
import styled from '@emotion/styled';
import { space } from 'styled-system';
import type { SpaceProps } from 'styled-system';

import type { Color } from 'common/theme/colors/types';
import type { BackgroundColorProps } from '../../theme/system-types';
import { cursor } from '../../theme/system-utilities';
import type { PropsWithRequiredChildren } from '../../types/React';
import { Button } from '../Button';
import { Flex } from '../Flex';
import type { IconName } from '../Icon';
import Icon from '../Icon';
import { SubtitleST4 } from '../Typography';

const ICON_SIZE = 24;

interface BadgeProps extends PropsWithRequiredChildren {
  iconName?: IconName;
  onIconClick?: (event: React.MouseEvent) => void;
  iconAriaLabel?: string;
  textColor?: Color;
  backgroundColor?: Color;
}

const BaseBadge = styled(Flex)<BackgroundColorProps>`
  border: none;
`;

const badgeStyles: ComponentProps<typeof BaseBadge> = {
  pl: 'sp_4',
  textAlign: 'left',
  alignItems: 'center',
  borderRadius: 'small',
  color: 'sys.warning.text.strong',
  justifyContent: 'space-between',
};

const IconButton = styled(Button)<SpaceProps>`
  ${space};
  ${cursor};
`;

const iconButtonStyles: ComponentProps<typeof IconButton> = {
  kind: 'bare',
  p: 'sp_0',
  ml: 'sp_4',
};

const BadgeIcon = ({
  iconName,
  onIconClick,
  iconAriaLabel,
  fill = 'sys.warning.text.strong',
}: Pick<BadgeProps, 'iconName' | 'onIconClick' | 'iconAriaLabel'> & { fill?: Color }) => {
  if (!iconName) return null;

  if (onIconClick) {
    return (
      <IconButton
        {...iconButtonStyles}
        onClick={onIconClick}
        data-testid="badge-icon-button"
        aria-label={iconAriaLabel}
      >
        <Icon name={iconName} size={ICON_SIZE} fill={fill} />
      </IconButton>
    );
  }

  return <Icon name={iconName} size={ICON_SIZE} fill={fill} styles={{ ml: 'sp_4' }} />;
};

export const Badge = ({
  children,
  iconName,
  onIconClick,
  iconAriaLabel,
  textColor = 'sys.warning.text.strong',
  backgroundColor = 'sys.warning.background.weak',
}: BadgeProps) => (
  <BaseBadge {...badgeStyles} backgroundColor={backgroundColor} data-testid="badge">
    <SubtitleST4 as="div" color={textColor} py="sp_2">
      {children}
    </SubtitleST4>
    <BadgeIcon
      fill={textColor}
      iconName={iconName}
      onIconClick={onIconClick}
      iconAriaLabel={iconAriaLabel}
    />
  </BaseBadge>
);
